import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import api from "@/lib/api";
import { motion } from "framer-motion";
import {
  Users, Target, Sparkles, Handshake, CheckCircle2, Gauge, TriangleAlert,
  Clock, DollarSign, PlayCircle, Share2,
} from "lucide-react";

function Stat({ icon: Icon, label, value, color, i }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 * i }}
      className="rounded-xl border border-white/10 bg-[var(--surface)] p-5">
      <div className="flex items-center justify-between">
        <span className="font-mono2 text-[10px] uppercase tracking-wider text-neutral-500">{label}</span>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <p className="font-head mt-3 text-3xl font-black text-white">{value}</p>
    </motion.div>
  );
}

export default function Admin() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <div className="min-h-screen"><Header /><p className="p-10 text-neutral-500">Loading control panel…</p></div>;

  const timeLabel = s.reported_time_saved_label || "0m";

  const stats = [
    { icon: Users, label: "Total Users", value: s.total_users, color: "#FF6B35" },
    { icon: Target, label: "Blockers Submitted", value: s.blockers_submitted, color: "#FF6B35" },
    { icon: Sparkles, label: "Moves Created", value: s.moves_created ?? s.recommendations_generated, color: "#A0A6B2" },
    { icon: Handshake, label: "Commitment Rate", value: `${s.commitment_rate}%`, color: "#10B981" },
    { icon: CheckCircle2, label: "Follow-Through", value: `${s.action_completion_rate}%`, color: "#10B981" },
    { icon: Gauge, label: "Actions Committed", value: s.actions_committed, color: "#FF6B35" },
    { icon: TriangleAlert, label: "Advice Meter Activations", value: s.advice_meter_activations, color: "#F43F5E" },
    { icon: Clock, label: "Reported Time Saved", value: timeLabel, color: "#A0A6B2" },
    { icon: DollarSign, label: "User-Reported Impact", value: `£${s.reported_financial_impact}`, color: "#10B981" },
    { icon: Share2, label: "Share Cards Generated", value: s.share_cards_generated, color: "#F59E0B" },
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <span className="eyebrow">MinuteSwap operational control</span>
        <h1 className="font-head mt-2 text-4xl font-black tracking-tight text-white">Admin Dashboard</h1>
        <p className="mt-2 text-neutral-400">Anonymised product analytics. Primary KPI: actions completed.</p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {stats.map((st, i) => <Stat key={st.label} {...st} i={i} />)}
        </div>

        <div className="mt-8 rounded-2xl border border-white/10 bg-[var(--surface)] p-6">
          <span className="eyebrow">Business categories · blocker distribution</span>
          <div className="mt-5 space-y-3">
            {(s.category_breakdown || []).map((c) => {
              const max = s.category_breakdown[0]?.count || 1;
              return (
                <div key={c.category} data-testid={`admin-cat-${c.category}`} className="flex items-center gap-4">
                  <span className="w-52 shrink-0 truncate text-sm text-neutral-300">{c.category}</span>
                  <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-white/5">
                    <div className="h-full rounded-full bg-[var(--amber)]" style={{ width: `${(c.count / max) * 100}%` }} />
                  </div>
                  <span className="font-mono2 w-8 text-right text-sm text-neutral-400">{c.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
